import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import type { StaffingViewAvailability } from "../viewAvailability";

/**
 * Single source of truth for whether the People roster is offered in this department: the
 * `staffing_people_tab` flag is on and the department is non-medical (medical departments run the
 * synergy grid and only ever offered the weekly view). Both the view-type selector and the route guard
 * consume this, so the People option is never offered where opening it would immediately redirect.
 *
 * Resolution order matters: the predicate is derived from the chosen department, so "the department
 * hasn't resolved yet" has to be answered BEFORE it is evaluated — `isMedicalDepartment` reads off
 * `DEFAULT_CHOSEN_DEPARTMENT` until then, and the flags are fetched per department.
 * `DEFAULT_CHOSEN_DEPARTMENT.department_id` is the empty string (contexts/ChosenDepartment/consts.ts),
 * so callers may assume a non-empty `department_id` whenever this is not `"loading"`.
 */
export const useStaffingPeopleViewAvailable = (): StaffingViewAvailability => {
	const isFlagEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingPeopleTab);
	const { chosenDepartment, isMedicalDepartment, isInitialLoading } = useHospitalData();

	if (!chosenDepartment.department_id || isInitialLoading) return "loading";

	return isFlagEnabled && !isMedicalDepartment ? "available" : "unavailable";
};
